/**
 * English dictionary. Keys are dot-separated by surface/feature for grep-ability.
 * Thai dictionary lives in `th.ts` with identical keys — the `useT` hook falls
 * back to English when a key is missing in the active locale, so it's safe to
 * ship a Thai translation that's only partially complete.
 */

export const en = {
  // Common / shared
  common: {
    back: 'Back',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    loading: 'Loading…',
    search: 'Search',
    close: 'Close',
    submit: 'Submit',
    all: 'All',
    error_generic: 'Something went wrong',
  },

  // Sidebar nav
  nav: {
    home: 'Home',
    explore: 'Explore',
    trending: 'Trending',
    all_tags: 'All tags',
    collections: 'Collections',
    following: 'Following',
    saved: 'Saved',
    my_prompts: 'My prompts',
    admin: 'Admin',
    new_prompt: 'New prompt',
    sign_in: 'Sign in',
    profile: 'Profile',
    libraries: 'Libraries',
    map: 'World map',
    my_reports: 'My reports',
  },

  // Top navbar (used by components/layout/Navbar.tsx)
  navbar: {
    explore: 'Explore',
    search_placeholder: 'Prompts, tags, or authors',
    clear_search: 'Clear search',
    sign_in: 'Sign In',
    new_prompt: 'New Prompt',
    account_menu: 'Account menu',
    view_profile: 'View profile',
    my_prompts: 'My prompts',
    saved: 'Saved',
    logout: 'Logout',
  },

  verify: {
    eyebrow: 'Email verification',
    checking: 'Confirming…',
    one_moment: 'One moment.',
    success_title: 'Email confirmed.',
    success_body: 'You can now post public prompts.',
    error_title: 'Couldn’t confirm.',
    expired_hint: 'The link may have expired. Sign in to request a fresh one.',
    back_home: 'Back to gallery',
    sign_in: 'Sign in',
    resend: 'Send a new link',
    resent: 'New link sent — check your inbox.',
  },

  forgot: {
    eyebrow: 'Password reset',
    title: 'Forgot your password?',
    subtitle: 'Enter the email on your account and we’ll send a reset link.',
    send_link: 'Send reset link',
    sending: 'Sending…',
    success_body: 'If that email is registered, a reset link is on its way. Check your inbox (and spam folder).',
    expiry_note: 'The link expires in 30 minutes.',
    back_to_login: 'Back to sign in',
    error_generic: 'Couldn’t send the link. Try again.',
  },

  reset: {
    eyebrow: 'Reset password',
    title: 'Choose a new password',
    subtitle: 'Pick something you’ll remember. Minimum 8 characters.',
    new_password: 'New password',
    confirm_password: 'Confirm new password',
    set_new_password: 'Set new password',
    saving: 'Saving…',
    success_toast: 'Password reset — you’re signed in.',
    error_no_token: 'This page needs a reset token. Open the link from your email.',
    error_too_short: 'Password must be at least 8 characters.',
    error_mismatch: 'Passwords don’t match.',
    error_generic: 'Reset failed. The link may have expired — request a new one.',
    back_to_login: 'Back to sign in',
  },

  reports: {
    eyebrow: 'Moderation',
    title: 'My reports',
    subtitle: 'Reports you’ve filed and the moderator’s decision.',
    empty: 'You haven’t filed any reports.',
    signin_required: 'Sign in to see the reports you’ve filed.',
    filed_on: 'Filed on {date}',
    resolution_note: 'Moderator note',
    status_open: 'Open',
    status_resolved: 'Resolved',
    status_dismissed: 'Dismissed',
    reason_spam: 'Spam',
    reason_abuse: 'Abuse',
    reason_copyright: 'Copyright',
    reason_nsfw: 'NSFW',
    reason_other: 'Other',
  },

  // World map (/map)
  map: {
    title: 'World map',
    stats: '{posts} prompts · {countries} countries',
    no_prompts: 'No prompts yet',
    n_prompts: '{count} prompts',
    one_prompt: '1 prompt',
    legend_less: 'Less',
    legend_more: 'More ({max})',
    zoom_in: 'Zoom in',
    zoom_out: 'Zoom out',
    zoom_reset: 'Reset zoom',
    close_panel: 'Close',
  },

  welcome: {
    eyebrow: 'Welcome',
    headline: 'Hi {name} — three places to start',
    body: 'Save prompts you like, follow people who post them, share your own. The bookmark icon on any prompt builds your private library — start with what’s trending, or pick a category that matches your work.',
    cta_trending: 'See trending',
    cta_explore: 'Explore categories',
    cta_share: 'Share your first prompt',
    dismiss_aria: 'Dismiss welcome',
    dismiss_title: 'Don’t show again',
  },

  // Top bar
  topbar: {
    search_placeholder: 'Search prompts, tags, or authors',
    sign_up: 'Sign up',
    view_profile: 'View profile',
    logout: 'Logout',
    language: 'Language',
    theme_toggle_light: 'Switch to light mode',
    theme_toggle_dark: 'Switch to dark mode',
    my_collections: 'My collections',
    my_prompts: 'My prompts',
  },

  // Home page
  home: {
    hero_title_before: 'The art of the',
    hero_title_em: 'perfect',
    hero_title_after: 'prompt.',
    hero_tagline: 'A curated gallery for creators, engineers, and writers to discover, save, and remix the prompts that actually work.',
    start_exploring: 'Start exploring',
    share_a_prompt: 'Share a prompt',
    stat_prompts_shared: 'Prompts shared',
    stat_copies_month: 'Copies this month',
    stat_creators: 'Creators',
    section_eyebrow: 'The collection',
    featured_prompts: 'Featured prompts',
    category_prompts: '{category} prompts',
    sort_featured: 'Featured',
    sort_newest: 'Newest',
    sort_top: 'Top',
    all_categories: 'All categories',
    category_art: 'Art',
    category_writing: 'Writing',
    category_coding: 'Coding',
    category_business: 'Business',
    category_education: 'Education',
    clear_tags: 'Clear',
    browse_tags: 'Browse tags',
    search_placeholder: 'Search prompts...',
    layout_masonry: 'Masonry layout',
    layout_uniform: 'Uniform grid',
    layout_list: 'List view',
    empty_no_prompts: 'No prompts match your search',
    empty_try_clearing: 'Try clearing filters or searching for something else.',
    load_more: 'Load more',
  },

  // Trending
  trending: {
    eyebrow: 'Trending now',
    title_before: 'What the world is',
    title_em: 'saving',
    title_after: '.',
    tagline: 'The prompts gaining the most momentum — updated hourly.',
    period_day: 'Today',
    period_week: 'This week',
    period_month: 'This month',
    ranking_eyebrow: 'The ranking',
    ranking_subtitle: 'Beyond the podium',
    also_trending: 'Also trending',
    full_grid: 'The full grid',
    empty: 'No trending prompts yet — come back later.',
    saves_label: 'saves',
  },

  // Following / feed
  following: {
    eyebrow: 'Your following',
    title_before: 'The people you',
    title_em: 'follow',
    tagline: 'Fresh prompts from your curators — newest first.',
    sort_newest: 'Newest',
    sort_top_rated: 'Top rated',
    sort_latest: 'Latest',
    empty_title: 'Your feed is quiet.',
    empty_body: 'Follow a few curators below and their new prompts will show up here.',
    suggest_heading: 'Curators people are loving',
    suggest_home: 'Curators to follow',
    see_feed: 'See your feed →',
    all_set: 'You’re all set.',
    your_feed: 'Your feed',
    following_count_one: '{count} creator',
    following_count_other: '{count} creators',
    new_prompts_summary: '{count} new prompts from people you trust · no algorithm, just chronology',
    discover: 'Discover',
    fresh_from_follows: 'Fresh from your follows',
    prompt_count_one: '{count} prompt',
    prompt_count_other: '{count} prompts',
    empty_no_new_title: 'No new prompts yet',
    empty_no_new_body: 'The creators you follow haven’t shared anything public recently.',
    sidebar_who_to_follow: 'Who to follow',
    sidebar_refresh: 'Refresh',
    sidebar_quote: '“Following is signal. Algorithms are noise.”',
    sidebar_quote_attr: '— The Prompt Save weekly',
    headline_following_em: 'Following',
  },

  // Auth
  auth: {
    welcome_back: 'Welcome back.',
    create_account: 'Create your account.',
    sign_in: 'Sign in',
    sign_up: 'Sign up',
    signing_in: 'Signing in…',
    creating_account: 'Creating account…',
    continue_google: 'Continue with Google',
    or: 'or',
    name: 'Name',
    username: 'Username',
    email: 'Email',
    password: 'Password',
    hint_no_spaces: 'no spaces',
    hint_min_8: 'min 8 characters',
    have_account: 'Already have an account?',
    new_to_site: 'New to Prompt Save?',
  },

  // Login / signup page (auth/login)
  login: {
    back_to_gallery: 'Back to gallery',
    quote: '“A great prompt is the smallest unit of applied imagination.”',
    quote_attr: '— from the Prompt Save manifesto',
    stat_prompts: 'prompts',
    stat_creators: 'creators',
    stat_copies_per_month: 'copies / mo',
    welcome_back_pre: 'Welcome',
    welcome_back_em: 'back',
    create_pre: 'Create your',
    create_em: 'account',
    sub_signup: 'Start saving, sharing, and remixing prompts.',
    sub_login: 'Sign in to your Prompt Save library.',
    name_placeholder: 'Your name',
    username_placeholder: 'yourusername',
    email_placeholder: 'you@domain.com',
    password_placeholder: '••••••••',
    forgot: 'Forgot?',
    create_account_btn: 'Create account',
    sign_in_btn: 'Sign in',
    creating_account: 'Creating account…',
    signing_in: 'Signing in…',
    have_account: 'Already have an account?',
    new_to_site: 'New to Prompt Save?',
    create_account_link: 'Create an account',
    signups_closed: 'New signups are currently closed.',
    error_login_failed: 'Login failed',
    error_register_failed: 'Registration failed',
  },

  // Prompt create/edit form
  prompt_form: {
    back: 'Back',
    error_title_required: 'Title is required.',
    error_content_required: 'Prompt content is required.',
    error_generic: 'Something went wrong.',
    title_label: 'Title',
    title_placeholder: 'e.g. Cinematic Portrait Generator',
    description_label: 'Description',
    optional: 'optional',
    description_placeholder: 'A short one-liner about what this prompt does.',
    content_label: 'Prompt Content',
    content_chars: '{count} chars',
    content_placeholder: 'Write your prompt here…\n\nTip: structure it like a brief — role, task, constraints, examples.',
    category_label: 'Category',
    images_label: 'Images',
    images_helper: 'First image = cover. Leave blank to use the auto-generated text preview.',
    models_label: 'AI Models',
    models_optional_max: 'optional · max 10',
    tags_label: 'Tags',
    tags_placeholder: 'Type and press Enter or comma…',
    visibility_label: 'Visibility',
    visibility_public: 'Public',
    visibility_public_desc: 'Listed in the gallery',
    visibility_private: 'Private',
    visibility_private_desc: 'Only you can see it',
    location_label: 'Location (optional)',
    location_placeholder: 'Search a place — city, landmark, address',
    location_hint: 'Pin this prompt at a specific spot on the world map. Leave blank to use your profile location.',
    location_clear: 'Clear',
    cancel: 'Cancel',
    saving: 'Saving…',
    live_preview: 'Live Preview',
    preview_subtitle: 'How your prompt will appear in the gallery.',
    preview_footer: 'Saves count and author avatar update in the real listing.',
  },

  // Profile
  profile: {
    eyebrow_curator: 'Curator',
    eyebrow_own: 'Your profile',
    eyebrow_system_bot: 'System bot',
    joined: 'Joined {date}',
    back: 'Back',
    edit_profile: 'Edit profile',
    new_prompt: 'New prompt',
    follow: 'Follow',
    following: 'Following',
    unfollow: 'Unfollow',
    unfollowing: 'Unfollowing…',
    following_progress: 'Following…',
    follow_error: 'Couldn’t update follow',
    stat_followers: 'Followers',
    stat_following: 'Following',
    stat_prompts: 'Prompts',
    stat_total_saves: 'Total saves',
    stat_total_copies: 'Total copies',
    stat_total_views: 'Total views',
    your_library: 'Your library',
    curated: 'Curated',
    your_collections: 'Your collections',
    collections: 'Collections',
    manage: 'Manage →',
    private: 'Private',
    section_eyebrow: 'Collection',
    empty_no_public: 'No public prompts yet',
    your_published_prompts: 'Your published prompts',
    published_by: 'Published by {name}',
    type_label: 'Type',
    tag_label: 'Tag',
    all: 'All',
    clear_filters: 'Clear filters',
    showing: 'Showing {count} of {total}',
    tag_title_one: '{count} prompt tagged #{name}',
    tag_title_other: '{count} prompts tagged #{name}',
    empty_filters: 'No prompts match these filters.',
    empty_publish_first: 'Publish your first prompt and it will appear here.',
    empty_user_no_public: '{name} hasn’t published any public prompts yet.',
    user_not_found: 'User not found',
    user_not_found_body: 'We couldn’t find anyone with that username.',
    back_to_gallery: '← Back to gallery',
    private_collection_count_one: '{count} prompt',
    private_collection_count_other: '{count} prompts',
  },

  // Prompt detail
  prompt_detail: {
    copy_prompt: 'Copy Prompt',
    copy: 'Copy',
    copied: 'Copied!',
    copied_short: 'Copied',
    copying: 'Copying…',
    save: 'Save',
    saved: 'Saved',
    remix: 'Remix',
    remix_of: 'Remix of',
    by: 'by',
    remix_tooltip: 'Remix this prompt — make your own version',
    the_prompt: 'The Prompt',
    customize: 'Customize',
    fill_blanks: 'Fill in the blanks',
    filled: 'filled',
    reset: 'Reset',
    stat_saves: 'Saves',
    stat_copies: 'Copies',
    stat_views: 'Views',
    back_to_gallery: 'Back to gallery',
    not_found: 'Prompt not found.',
    works_with: 'Works with',
    open_model_details: 'Open {name} details',
    sign_in_to_save: 'Sign in to save',
    saved_to_collection: 'Saved to your collection',
    removed_from_collection: 'Removed from your collection',
    copied_to_clipboard: 'Copied to clipboard',
    copied_with_values: 'Copied with {count} value(s) filled',
    clipboard_error: 'Couldn’t access clipboard',
    ready_to_copy: 'Ready to copy',
    preview_with_values: 'Preview with your values',
    click_to_fill: 'Click to fill {name}',
    tip_leave_blank_pre: 'Tip: Leave blank to keep the original',
    tip_leave_blank_post: 'in the copied text.',
    lineage: 'Lineage',
    fork_tree: 'Fork tree',
    view_profile_of: 'View {name}’s profile',
  },

  // Library detail (/library/[slug])
  library: {
    not_found_title: 'Library not found',
    not_found_msg: 'Library not found.',
    browse_libraries: 'Browse libraries',
    breadcrumb_home: 'Home',
    breadcrumb_libraries: 'Libraries',
    stat_prompts: 'prompts',
    stat_contributors: 'contributors',
    stat_saves_month: 'saves this month',
    follow_library: 'Follow library',
    share_a_prompt: 'Share a {name} prompt',
    all_topics: 'All topics',
    editors_pick: 'Editor’s pick',
    open_prompt: 'Open prompt',
    more_in: 'More in {name}',
    the_collection: 'The collection',
    sort_top: 'Top',
    sort_new: 'New',
    sort_most_remixed: 'Most remixed',
    empty_tag: 'No prompts match that tag yet.',
    empty_more: 'No more prompts yet.',
    section_people: 'People',
    top_contributors: 'Top contributors',
    followers_suffix: 'followers',
    saved_suffix: 'saved',
  },

  // Model detail (/models/[...id])
  model_detail: {
    not_found_title: 'Model not found',
    not_found_body_pre: 'We couldn’t find an AI model called',
    not_found_body_post: 'in the registry.',
    back_to_models: 'Back to all models',
    browse_prompts: 'Browse prompts',
    loading: 'Loading…',
    all_models: 'All models',
    eyebrow: 'Prompts',
    designed_for: 'Designed for {name}',
    sort_featured: 'Featured',
    sort_newest: 'Newest',
    sort_top: 'Top',
    stat_prompts: 'Prompts',
    stat_hint: 'using this model',
    no_prompts_title: 'No prompts for {name} yet',
    no_prompts_body: 'Be the first — share a prompt designed for this model.',
    share_a_prompt: 'Share a prompt',
    empty_message: 'No prompts use {name} yet.',
    by_brand: 'by',
  },

  // Explore page
  explore: {
    eyebrow: 'Browse the library',
    title_before: 'Find prompts',
    title_em: 'by craft',
    title_after: '.',
    tagline: 'Categories curated by working practitioners. Each one is a living collection, not a dumping ground.',
    browse: 'Browse',
    rising_eyebrow: 'This week',
    rising_title: 'Rising prompts',
    view_all: 'View all',
  },

  // Tags page
  tags: {
    back_to_gallery: 'Back to gallery',
    eyebrow: 'Every tag in the gallery',
    title_before: 'The vocabulary',
    title_em: 'of prompts',
    title_after: '.',
    loading_directory: 'Loading the directory…',
    summary_top_tag: 'top tag has',
    summary_tail: 'The shorthand curators use to organize their work — pick one to see matching prompts.',
    summary_tags_label: 'tags',
    summary_prompts_label: 'prompts',
    search_placeholder_loading: 'Loading…',
    search_placeholder: 'Search {count} tags…',
    clear_filter: 'Clear filter',
    editors_pick: 'Editor’s pick',
    most_used: 'Most used',
    directory: 'Directory',
    matching: 'Matching "{query}"',
    all_tags: 'All tags',
    sort_featured: 'Featured',
    sort_by_count: 'By count',
    sort_alpha: 'A–Z',
    no_match_title: 'No tags match "{query}"',
    no_match_body: 'Try a different keyword, or clear the filter to see all {total} tags.',
  },

  // Collections page
  collections: {
    eyebrow: 'Collections',
    title_before: 'Curated',
    title_em: 'playlists',
    title_after: 'of prompts.',
    tagline: 'Public collections from across Prompt Save — themed packs, workflow kits, and editorial favorites.',
    sort_newest: 'Newest',
    sort_largest: 'Largest',
    search_placeholder: 'Search by name or description…',
    clear_search: 'Clear search',
    empty_match_title: 'No collections match that search.',
    empty_no_public_title: 'No public collections yet.',
    empty_match_body: 'Try different keywords, or clear the search.',
    empty_no_public_body: 'Be the first — make a collection and set it to public.',
    load_more: 'Load more',
    showing_of: 'Showing {shown} of {total}',
  },

  // Cookie consent modal
  cookie: {
    title: 'Your privacy choices',
    intro:
      'We use cookies to keep the site working, remember your preferences, and (with your permission) measure traffic and serve relevant ads. You can change these any time from the footer.',
    necessary_label: 'Necessary',
    necessary_desc: 'Required for sign-in, security, and core features. Always on.',
    analytics_label: 'Analytics',
    analytics_desc: 'Anonymous usage stats so we can see what to improve.',
    advertising_label: 'Advertising',
    advertising_desc: 'Used by Google AdSense to show relevant ads and limit repetition.',
    accept_all: 'Accept all',
    reject_non_essential: 'Reject non-essential',
    save_preferences: 'Save preferences',
    learn_more: 'Read our Cookie Policy',
    settings_button: 'Cookie settings',
    always_on: 'Always on',
  },

  // Footer
  footer: {
    tagline: 'A gallery of great prompts.',
    section_product: 'Product',
    section_legal: 'Legal',
    section_resources: 'Resources',
    link_explore: 'Explore',
    link_trending: 'Trending',
    link_collections: 'Collections',
    link_tags: 'Tags',
    link_about: 'About',
    link_privacy: 'Privacy Policy',
    link_terms: 'Terms of Service',
    link_cookies: 'Cookie Policy',
    link_guidelines: 'Community Guidelines',
    link_dmca: 'DMCA',
    link_disclaimer: 'Disclaimer',
    link_security: 'Security',
    copyright: '© {year} {site}. All rights reserved.',
    made_with: 'Made for creators, engineers, and writers.',
  },

  // Shared UI for legal/static pages
  legal: {
    last_updated: 'Last updated: {date}',
    back_home: 'Back to home',
    contents: 'On this page',
  },

  // 404 / 500 pages
  errors: {
    not_found_title: 'Page not found',
    not_found_body: "We couldn't find the page you were looking for. It may have been moved or removed.",
    server_title: 'Something went wrong',
    server_body: 'An unexpected error occurred while loading this page. Try again, or head back home.',
    try_again: 'Try again',
  },

  // ── Admin console ───────────────────────────────────────────────────
  admin_common: {
    loading: 'Loading…',
    saving: 'Saving…',
    save: 'Save',
    save_changes: 'Save changes',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    create: 'Create',
    close: 'Close',
    search: 'Search',
    reset: 'Reset',
    export: 'Export',
    download: 'Download',
    actions: 'Actions',
    status: 'Status',
    when: 'When',
    target: 'Target',
    name: 'Name',
    role: 'Role',
    created: 'Created',
    last_login: 'Last login',
    page_of: 'Page {page} of {total}',
    previous: 'Previous',
    next: 'Next',
    prev_arrow: '← Prev',
    next_arrow: 'Next →',
    all: 'All',
    none: '—',
    yes: 'yes',
    no: 'no',
    on: 'ON',
    off: 'OFF',
    enabled: 'Enabled',
    disabled: 'Disabled',
    active: 'Active',
    inactive: 'Inactive',
    hidden: 'Hidden',
    published: 'Published',
    private: 'Private',
    optional: 'optional',
    failed_generic: 'Failed',
    save_failed: 'Save failed',
    couldnt_load: 'Couldn’t load',
    couldnt_delete: 'Couldn’t delete',
    couldnt_update: 'Couldn’t update',
    moderator_readonly: 'Moderator role can view but not edit {resource}. Ask an admin or super_admin to make changes.',
  },

  admin_root: {
    eyebrow: 'Console',
    title: 'Admin',
    signed_in_as: 'Signed in as',
    role_super_admin: 'Super admin',
    role_admin: 'Admin',
    role_moderator: 'Moderator',
    export_report: 'Export report',
    manage_admins: 'Manage admins',
    sign_out: 'Sign out',
    sign_out_title: 'Sign out of the admin console only — your regular Prompt Save session is unaffected.',
    report_exported: 'Report exported',
    couldnt_export: 'Couldn’t export',
    metric_total_users: 'Total users',
    metric_total_admins: 'Total admins',
    metric_total_prompts: 'Total prompts',
    metric_public_prompts: 'Public prompts',
    metric_private_prompts: 'Private prompts',
    metric_total_saves: 'Total saves',
  },

  admin_sidebar: {
    menu: 'Menu',
    open_nav_aria: 'Open admin navigation',
    sections_aria: 'Admin sections',
    group_overview: 'Overview',
    group_content: 'Content',
    group_users: 'Users',
    group_moderation: 'Moderation',
    group_system: 'System',
    item_dashboard: 'Dashboard',
    item_online: 'Online',
    item_prompts: 'Prompts',
    item_libraries: 'Libraries',
    item_aimodels: 'AI models',
    item_announcements: 'Announcements',
    item_users: 'Site users',
    item_admins: 'Admins',
    item_reports: 'Reports',
    item_comments: 'Comments',
    item_abuse: 'Abuse events',
    item_audit: 'Audit log',
    item_config: 'Config',
    item_email: 'Email',
    item_database: 'Database',
    item_consent: 'Consent log',
    item_retention: 'Retention',
    item_seed_pool: 'Seed pool',
    item_subscriptions: 'Subscriptions',
    badge_pending_aria: '{count} items pending',
  },

  admin_login: {
    badge: 'Admin area',
    title: 'Sign in to admin',
    subtitle: 'This login is separate from your regular Prompt Save account. Only provisioned admin accounts can sign in here.',
    cross_session_pre: 'You\'re signed in as',
    cross_session_post: 'on the regular site. Admin is a separate system — your site password won\'t work here. If you don\'t have an admin account,',
    return_to_site: 'return to the site',
    or_word: 'Or',
    sign_out_site: 'sign out of the site',
    if_you_dont_need: 'if you don\'t need both sessions.',
    field_identifier: 'Username or email',
    field_password: 'Password',
    identifier_placeholder: 'admin_username or admin@example.com',
    sign_in: 'Sign in',
    signing_in: 'Signing in…',
    back_to_site: '← Back to site',
    logged_notice: 'All sign-in attempts are logged.',
    error_login_failed: 'Login failed',
  },

  admin_dashboard: {
    metric_total_prompts: 'Total prompts',
    metric_total_users: 'Total users',
    metric_private_drafts: 'Private drafts',
    metric_avg_saves: 'Avg. saves / prompt',
    hint_last_days: 'last {days} days',
    hint_members: 'members',
    hint_not_published: 'not published',
    hint_all_time: 'all time',
    hint_n_admins_one: '{count} admin',
    hint_n_admins_other: '{count} admins',
    hint_private_hidden: '{count} hidden from search',
    submissions_title: 'Prompt submissions',
    submissions_sub: 'Last {days} days · daily',
    spark_published: 'Published',
    spark_private: 'Private',
    spark_saves: 'Saves',
    top_categories: 'Top categories',
    top_categories_sub: 'By published count',
    no_categories: 'No categories yet.',
    recent_activity: 'Recent activity',
    recent_activity_sub: 'Across the platform · last {count} events',
    no_activity: 'No activity yet.',
    couldnt_load: 'Couldn’t load dashboard data.',
  },

  admin_prompts: {
    filter_all: 'All',
    filter_published: 'Published',
    filter_private: 'Private',
    search_placeholder: 'Search prompts by title or content',
    export: 'Export',
    generate: 'Generate',
    generate_title: 'Auto-generate publishable prompts attributed to a chosen user',
    n_selected: '{count} selected',
    bulk_clear: 'Clear',
    bulk_hide: 'Hide',
    bulk_unhide: 'Unhide',
    bulk_queue: 'Move to queue',
    bulk_delete: 'Delete',
    bulk_delete_title: 'Bulk delete is restricted to super_admin',
    col_prompt: 'Prompt',
    col_author: 'Author',
    col_category: 'Category',
    col_saves: 'Saves',
    col_views: 'Views',
    col_status: 'Status',
    no_match: 'No prompts match.',
    status_hidden: 'hidden',
    status_hidden_title: 'Hidden by admin — not visible in public views',
    status_published: 'published',
    status_private: 'private',
    type_remix: 'Remix',
    type_image_cover: 'Image cover',
    type_text_prompt: 'Text prompt',
    cell_other: 'Other',
    title_unhide: 'Unhide — make visible to everyone',
    title_hide: 'Hide from public — keeps ownership',
    title_delete: 'Delete prompt permanently',
    total_prompts: '{count} total prompts',
    confirm_delete_title: 'Delete "{title}"?',
    confirm_delete_body: 'This cannot be undone.',
    confirm_bulk_delete_title: 'Soft-delete {count} prompt(s)?',
    confirm_bulk_delete_body: 'Restorable from /admin#prompts (private filter).',
    select_all_aria: 'Select all visible',
    prompt_deleted: 'Prompt deleted',
    couldnt_delete: 'Couldn’t delete',
    couldnt_update_visibility: 'Couldn’t update visibility',
    hidden_toast: 'Hidden "{title}" from the public site',
    restored_toast: 'Restored "{title}" to public',
    exported_n: 'Exported {count} prompt(s)',
    generated_n: 'Generated {count} prompt(s)',
    bulk_hidden: 'Hidden {affected} of {requested}',
    bulk_unhidden: 'Unhidden {affected} of {requested}',
    bulk_queued: 'Queued {affected} of {requested}',
    bulk_deleted: 'Deleted {affected} of {requested}',
    hide_reason_prompt: 'Hide reason (spam / abuse / copyright / nsfw / other):',
  },

  admin_users: {
    search_placeholder: 'Search users by name, handle or email',
    admins_note_pre: 'Admins are managed in the',
    admins_note_tab: 'Admins',
    admins_note_post: 'tab — users and admins are separate.',
    col_user: 'User',
    col_prompts: 'Prompts',
    col_saved: 'Saved',
    col_provider: 'Provider',
    col_joined: 'Joined',
    no_match: 'No users match.',
    suspend_placeholder: 'Suspend…',
    suspend_title: 'Suspend this user — pick a duration',
    unsuspend: 'Unsuspend',
    unsuspend_title: 'Lift the suspension',
    delete_user_title: 'Delete user',
    permaban: 'Permaban',
    suspended: 'Suspended',
    total_users: '{count} total users',
    confirm_delete_title: 'Delete account @{username}?',
    confirm_delete_body: 'This permanently removes their prompts too.',
    confirm_suspend_title: 'Suspend @{username} {label}?',
    confirm_suspend_body: 'They can still log in + browse, but every write endpoint refuses with 403.',
    suspend_label_perma: 'permanently',
    suspend_label_for: 'for {label}',
    suspend_reason_prompt: 'Reason (shown to admins, NOT to the user):',
    duration_1h: '1 hour',
    duration_24h: '24 hours',
    duration_7d: '7 days',
    duration_30d: '30 days',
    duration_perma: 'Permanent',
    user_deleted: 'User deleted',
    couldnt_delete_user: 'Couldn’t delete user',
    suspended_until: 'Suspended until {when}',
    suspended_perma: 'Suspended permanently',
    couldnt_suspend: 'Couldn’t suspend',
    couldnt_unsuspend: 'Couldn’t unsuspend',
    unsuspended_toast: 'Unsuspended @{username}',
    rem_minutes: '{count}m left',
    rem_hours: '{count}h left',
    rem_days: '{count}d left',
    rem_expiring: 'expiring…',
  },

  admin_admins: {
    search_placeholder: 'Search admins',
    new_admin: 'New admin',
    only_super_note: 'Only',
    super_admin: 'super_admin',
    only_super_note_post: 'accounts can create, edit, or remove admins. You can still view the list.',
    col_admin: 'Admin',
    no_admins: 'No admins found.',
    total_count_one: '{count} admin',
    total_count_other: '{count} admins',
    title_deactivate: 'Deactivate',
    title_reactivate: 'Reactivate',
    title_edit: 'Edit admin',
    title_delete: 'Delete admin',
    confirm_delete_title: 'Delete admin @{username}?',
    confirm_delete_body: 'This does not affect any site users.',
    cant_delete_self: 'You can’t delete your own admin account.',
    cant_deactivate_self: 'You can’t deactivate yourself.',
    removed_toast: '{name} removed',
    reactivated_toast: '{name} reactivated',
    deactivated_toast: '{name} deactivated',
    couldnt_load: 'Couldn’t load admins',
    couldnt_delete: 'Couldn’t delete',
    couldnt_update: 'Couldn’t update',
    dlg_new: 'New admin',
    dlg_edit: 'Edit admin',
    dlg_create_title: 'Create admin account',
    dlg_edit_title: 'Edit {name}',
    dlg_field_username: 'Username',
    dlg_field_display_name: 'Display name',
    dlg_field_email: 'Email (optional)',
    dlg_field_password_new: 'Password (10+ chars)',
    dlg_field_password_edit: 'New password (leave blank to keep)',
    dlg_field_role: 'Role',
    dlg_field_status: 'Status',
    dlg_role_moderator: 'Moderator — read + hide prompts',
    dlg_role_admin: 'Admin — moderation + dashboard',
    dlg_role_super_admin: 'Super admin — everything (incl. admin management)',
    dlg_status_active: 'Active (can sign in)',
    dlg_status_inactive: 'Inactive (sign-in blocked)',
    dlg_username_ph: 'e.g. alice_admin',
    dlg_name_ph: 'Alice Admin',
    dlg_email_ph: 'alice@example.com',
    dlg_password_ph_new: 'at least 10 characters',
    dlg_create_btn: 'Create admin',
    dlg_validation_required: 'Username, name, and 10+ char password required',
    admin_created: 'Admin created',
    admin_updated: 'Admin updated',
    status_active: 'active',
    status_inactive: 'inactive',
  },

  admin_audit: {
    search_placeholder: 'Search by admin, target, or action',
    filter_all_actions: 'All actions',
    filter_last_24h: 'Last 24 hours',
    filter_last_7d: 'Last 7 days',
    filter_last_30d: 'Last 30 days',
    filter_last_90d: 'Last 90 days',
    filter_all_time: 'All time',
    n_entries_one: '{count} entry',
    n_entries_other: '{count} entries',
    col_admin: 'Admin',
    col_action: 'Action',
    col_target: 'Target',
    col_ip: 'IP',
    no_match: 'No audit entries match.',
    details: 'details',
  },

  admin_reports: {
    filter_open: 'open',
    filter_resolved: 'resolved',
    filter_dismissed: 'dismissed',
    col_reason: 'Reason',
    col_target: 'Target',
    col_reporter: 'Reporter',
    col_action: 'Action',
    no_match: 'No reports match.',
    btn_hide: 'Hide',
    btn_hide_title: 'Resolve + hide the target',
    btn_resolve: 'Resolve',
    btn_resolve_title: 'Mark resolved without hiding',
    btn_dismiss: 'Dismiss',
    btn_dismiss_title: 'No action — false report',
    resolved_by: 'Resolved by @{username}: {note}',
  },

  admin_comments: {
    filter_live: 'live',
    filter_hidden: 'hidden',
    filter_deleted: 'deleted',
    search_placeholder: 'Search comment text',
    n_comments_one: '{count} comment',
    n_comments_other: '{count} comments',
    col_comment: 'Comment',
    col_on_prompt: 'On prompt',
    no_match: 'No comments match.',
    deleted_by_user: 'deleted by user',
    deleted_prompt: '(deleted prompt)',
    btn_unhide: 'Unhide',
    btn_hide: 'Hide',
    hard_delete_title: 'Hard-delete (permanent)',
    hide_reason_prompt: 'Hide reason for this comment?\n{options}',
    confirm_hard_delete_title: 'Hard-delete this comment?',
    confirm_hard_delete_body: '"{snippet}"\n\nThis can\'t be undone — soft-hide is usually the right answer.',
    confirm_hard_delete_btn: 'Hard-delete',
    comment_hidden: 'Comment hidden',
    comment_restored: 'Comment restored',
    comment_deleted_perm: 'Comment deleted permanently',
  },

  admin_abuse: {
    filter_ip_placeholder: 'Filter by IP — e.g. 1.2.3.4',
    n_events_one: '{count} event',
    n_events_other: '{count} events',
    col_kind: 'Kind',
    col_label: 'Label',
    col_endpoint: 'Endpoint',
    no_match: 'No abuse signals match. Either nobody is misbehaving or the filters are too narrow.',
    details: 'details',
    title_filter_ip: 'Filter by this IP',
  },

  admin_config: {
    title: 'Site configuration',
    subtitle: 'Runtime-editable toggles & copy. Public keys are also exposed at',
    save_n: 'Save ({count})',
    moderator_readonly_note: 'Moderator accounts have read-only access to site settings. Ask an admin or super_admin to apply changes.',
    not_writable_title: 'Moderators can’t change site settings',
    saved_n_one: 'Saved {count} setting',
    saved_n_other: 'Saved {count} settings',
    couldnt_load: 'Couldn’t load settings',
    loading_settings: 'Loading settings…',
  },

  admin_libraries: {
    title: 'Libraries',
    subtitle: 'Editorialized category pages. Toggle visibility, edit visuals, change ordering.',
    new_library: 'New library',
    empty: 'No libraries yet. Click {btn} to create one.',
    couldnt_load: 'Couldn’t load libraries',
    couldnt_delete: 'Couldn’t delete',
    couldnt_update: 'Couldn’t update library',
    confirm_delete_title: 'Delete the "{name}" library?',
    confirm_delete_body: 'Prompts are kept; only the landing page is removed.',
    deleted_toast: '{name} deleted',
    enabled_toast: '{name} enabled',
    disabled_toast: '{name} disabled',
    tile_enabled: 'Enabled',
    tile_hidden: 'Hidden',
    tile_edit: 'Edit',
    tile_delete_title: 'Delete library',
    dlg_new: 'New',
    dlg_edit: 'Edit',
    dlg_create_title: 'Create library',
    dlg_edit_title: 'Edit {name}',
    dlg_field_name: 'Name',
    dlg_field_name_hint: 'e.g. Writing, Photography',
    dlg_field_slug: 'URL slug',
    dlg_field_slug_hint: 'a-z, 0-9, dashes. Leave empty to auto-generate from name.',
    dlg_field_slug_ph: 'e.g. writing',
    dlg_field_category: 'Prompt category',
    dlg_field_category_hint: 'Which Prompt.category value this filters by. Usually same as name.',
    dlg_field_category_ph: 'e.g. Writing',
    dlg_field_description: 'Description',
    dlg_field_description_hint: 'One paragraph of editorial framing for the banner.',
    dlg_field_hue: 'Hue (0-360)',
    dlg_field_hue_hint: 'Tint of the banner gradient.',
    dlg_field_icon: 'Icon',
    dlg_field_icon_hint: 'Material Symbols name.',
    dlg_field_icon_ph: 'e.g. edit_note',
    dlg_field_cover: 'Cover image URL',
    dlg_field_cover_hint: 'Optional — overrides the hue gradient on the banner.',
    dlg_field_sort: 'Sort order',
    dlg_field_sort_hint: 'Lower numbers appear first.',
    dlg_field_visibility: 'Visibility',
    dlg_visibility_enabled: 'Enabled (public)',
    dlg_visibility_hidden: 'Hidden (admin-only)',
    dlg_create_btn: 'Create library',
    dlg_validation: 'Name and category are required',
    library_created: 'Library created',
    library_updated: 'Library updated',
  },

  admin_ai_models: {
    search_placeholder: 'Search by id, name, or brand',
    filter_all_categories: 'All categories',
    cat_text: 'Text & Chat',
    cat_image: 'Image',
    cat_video: 'Video',
    cat_voice: 'Voice & Audio',
    cat_code: 'Code & Agents',
    showing_count: '{shown} of {total}',
    new_model: 'New AI model',
    moderator_note: 'Moderator role can view the registry but not edit. Ask an admin or super_admin to make changes.',
    col_model: 'Model',
    col_brand: 'Brand',
    col_category: 'Category',
    col_color: 'Color',
    col_order: 'Order',
    no_match: 'No models match.',
    status_enabled: 'enabled',
    status_disabled: 'disabled',
    title_disable: 'Disable',
    title_enable: 'Enable',
    title_edit: 'Edit',
    title_delete: 'Delete',
    couldnt_load: 'Couldn’t load AI models',
    confirm_delete_title: 'Delete "{name}" ({id})?',
    confirm_delete_body: 'Prompts that reference this model will still work — they just won\'t pretty-print the chip until you re-add the model or remove the reference.',
    removed_toast: '{name} removed',
    enabled_toast: '{name} enabled',
    disabled_toast: '{name} disabled',
    couldnt_delete: 'Couldn’t delete',
    couldnt_update: 'Couldn’t update',
    dlg_new: 'New AI model',
    dlg_edit: 'Edit AI model',
    dlg_create_title: 'Add a new model',
    dlg_edit_title: 'Edit {name}',
    dlg_preview_label: 'Preview:',
    dlg_preview_default: 'Model name',
    dlg_field_id: 'ID (slug)',
    dlg_field_id_ph: 'e.g. openai/gpt-4o',
    dlg_field_id_locked_title: 'ID is immutable — prompts reference this slug',
    dlg_field_id_hint_new: 'Format: vendor/model-name. Lowercase, no spaces. Cannot be changed later.',
    dlg_field_id_hint_edit: 'Slug is locked once created — prompts reference it.',
    dlg_field_name: 'Display name',
    dlg_field_name_ph: 'e.g. GPT-4o',
    dlg_field_brand: 'Brand',
    dlg_field_brand_ph: 'e.g. OpenAI',
    dlg_field_category: 'Category',
    dlg_field_sort: 'Sort order',
    dlg_field_color: 'Brand color (hex)',
    dlg_field_icon: 'Icon (optional)',
    dlg_field_icon_ph: 'Material Symbols name (e.g. smart_toy)',
    dlg_field_status: 'Status',
    dlg_status_enabled: 'Enabled (selectable on /create)',
    dlg_status_disabled: 'Disabled (hidden from authors)',
    dlg_create_btn: 'Create model',
    dlg_validation_required: 'Name and brand are required',
    dlg_validation_id: 'ID must be vendor/model-name format (e.g. openai/gpt-4o)',
    dlg_validation_exists: 'Model id "{id}" already exists',
    model_created: 'Model created',
    model_updated: 'Model updated',
  },

  admin_database: {
    super_only_title: 'Super-admin only',
    super_only_body: 'Database tooling exposes raw schema and row data. Only super_admin accounts can open this tab.',
    sub_tables: 'Tables',
    sub_diff: 'Diff',
    sub_transfer: 'Transfer',
    n_tables: '{count} tables',
    n_columns_rows: '{cols} columns · {rows} rows',
    redacted: 'redacted: {cols}',
    col_column: 'Column',
    col_type: 'Type',
    col_nullable: 'Nullable',
    col_pk: 'PK',
    col_default: 'Default',
    sample_rows: 'Sample rows · page {page}/{total}',
    no_rows: 'No rows.',
    diff_title: 'Schema diff',
    diff_subtitle: 'Compare this instance\'s tables and columns against another Prompt Save instance. Doesn\'t copy any data — read-only.',
    field_remote_url: 'Remote API base URL',
    field_remote_url_ph: 'https://staging.example.com/api',
    field_remote_token: 'Admin JWT issued on the remote',
    field_remote_token_super: 'Admin JWT issued on the remote (super_admin)',
    field_remote_token_ph: 'eyJhbGciOiJIUzI1Ni…',
    btn_run_diff: 'Run diff',
    btn_comparing: 'Comparing…',
    diff_validation: 'Both remote URL and token are required',
    diff_failed: 'Diff failed',
    result_title: 'Result',
    metric_in_both: 'In both',
    metric_with_diffs: 'With diffs',
    metric_only_here: 'Only here',
    metric_only_there: 'Only there',
    only_here_count: 'Only here ({count})',
    only_there_count: 'Only on remote ({count})',
    tables_in_both_count: 'Tables in both ({count})',
    only_here_lc: 'only here:',
    only_there_lc: 'only there:',
    type_mismatches_lc: 'type mismatches:',
    rows_summary: 'rows: {here} here · {there} there',
    transfer_title: 'Transfer table data',
    transfer_subtitle_pre: 'Push every row of one local table to a remote Prompt Save instance.',
    transfer_subtitle_warn: 'Always dry-run first.',
    transfer_subtitle_post: 'Tables admins and audit_logs are blocked.',
    field_table: 'Table',
    field_table_ph: '— pick a table —',
    field_table_option: '{name} ({rows} rows)',
    field_mode: 'Mode',
    mode_replace: 'Replace — delete remote rows then insert',
    mode_merge: 'Merge — upsert by primary key',
    btn_dry_run: 'Dry run',
    btn_transfer: 'Transfer for real',
    transfer_confirm_title: 'Transfer "{table}" to {url}?',
    transfer_confirm_body: 'Mode: {mode}\n{warning}\n\nType the dry-run first if you haven\'t already. Continue?',
    transfer_warn_replace: 'This DELETES all remote rows in this table first.',
    transfer_warn_merge: 'Existing remote rows are upserted by primary key.',
    transfer_validation: 'Remote URL, token, and table are required',
    transfer_failed: 'Transfer failed',
    transferred_toast: 'Transferred {count} rows',
    dry_run_preview: 'Dry-run preview',
    transfer_complete: 'Transfer complete',
    metric_local_rows: 'Local rows',
    metric_remote_before: 'Remote before',
    metric_remote_after: 'Remote after',
    metric_transferred: 'Transferred',
    metric_skipped: 'Skipped',
    n_errors_one: '{count} error:',
    n_errors_other: '{count} errors:',
    couldnt_load_tables: 'Couldn’t load tables',
    couldnt_load_table: 'Couldn’t load table',
    couldnt_load_rows: 'Couldn’t load rows',
  },

  admin_announcements: {
    summary: '{active} active · {total} total',
    new_announcement: 'New announcement',
    col_banner: 'Banner',
    col_severity: 'Severity',
    col_audience: 'Audience',
    col_created: 'Created',
    by_username: 'by @{username}',
    empty: 'No announcements yet. Click {btn} to broadcast something.',
    status_live: 'live',
    status_hidden: 'hidden',
    title_hide: 'Hide',
    title_activate: 'Activate',
    title_edit: 'Edit',
    title_delete: 'Delete',
    confirm_delete_title: 'Delete "{title}"?',
    confirm_delete_body: 'It will disappear from the public site immediately.',
    couldnt_load: 'Couldn’t load announcements',
    couldnt_update: 'Couldn’t update',
    couldnt_delete: 'Couldn’t delete',
    deleted_toast: 'Deleted',
    dlg_new: 'New announcement',
    dlg_edit: 'Edit announcement',
    dlg_create_title: 'Broadcast a banner',
    dlg_edit_title: 'Edit "{title}"',
    dlg_field_title: 'Title',
    dlg_field_title_ph: 'Maintenance scheduled',
    dlg_field_body: 'Body',
    dlg_field_body_ph: 'Short, plain-language explanation.',
    dlg_field_severity: 'Severity',
    dlg_field_audience: 'Audience',
    severity_info: 'Info — neutral, default',
    severity_success: 'Success — green, celebratory',
    severity_warning: 'Warning — amber, advisory',
    severity_alert: 'Alert — red, blocking',
    audience_everyone: 'Everyone',
    audience_authed: 'Signed-in users only',
    audience_guests: 'Guests only',
    dlg_field_cta_label: 'CTA label (optional)',
    dlg_field_cta_label_ph: 'Read more',
    dlg_field_cta_href: 'CTA URL (optional)',
    dlg_field_cta_href_ph: '/changelog or https://…',
    dlg_dismissible: 'Users can dismiss',
    dlg_active: 'Active (live on the public site)',
    dlg_create_btn: 'Create banner',
    dlg_preview_title: 'Banner title',
    dlg_preview_body: 'Banner body — what users will read.',
    dlg_validation: 'Title and body are required',
    announcement_created: 'Announcement created',
    announcement_updated: 'Announcement updated',
  },

  admin_email: {
    sub_templates: 'Templates',
    sub_send: 'Send',
    sub_log: 'Log',
    n_templates: '{count} templates',
    couldnt_load_templates: 'Couldn’t load templates',
    section_subject: 'Subject',
    section_body: 'Body',
    section_variables: 'Variables',
    send_title: 'Send email',
    send_subtitle: 'Always dry-run first. Server logs every dispatch in the {tab} tab and writes an audit row.',
    field_template: 'Template',
    field_template_ph: '— pick a template —',
    field_template_option: '{category} · {label}',
    field_vars: 'Variables (substituted into the template)',
    field_recipient: 'Recipient',
    target_single: 'Single address',
    target_all: 'Broadcast to all users',
    recipient_email_ph: 'someone@example.com',
    target_all_note: 'Sends to every user with an email on file. Capped at 500 recipients server-side.',
    btn_dry_run: 'Dry run',
    btn_send: 'Send',
    val_pick_template: 'Pick a template',
    val_recipient_required: 'Recipient email required',
    confirm_broadcast_title: 'Broadcast to EVERY user?',
    confirm_broadcast_body: 'This may send hundreds of emails.\n\nClick Cancel to dry-run first.',
    confirm_broadcast_btn: 'Send broadcast',
    sent_summary: 'Sent {sent} · failed {failed}',
    sent_summary_skipped: 'Sent {sent} · failed {failed} · skipped {skipped}',
    send_failed: 'Send failed',
    dry_run_preview: 'Dry-run preview',
    send_result: 'Send result',
    metric_targets: 'Targets',
    metric_sent: 'Sent',
    metric_failed: 'Failed',
    metric_skipped: 'Skipped',
    sample_subject: 'Sample subject',
    sample_body: 'Sample body',
    log_filter_all_statuses: 'All statuses',
    log_status_sent: 'Sent',
    log_status_failed: 'Failed',
    log_status_skipped: 'Skipped (dev)',
    log_status_queued: 'Queued',
    log_filter_24h: 'Last 24h',
    log_filter_7d: 'Last 7 days',
    log_filter_30d: 'Last 30 days',
    log_filter_all: 'All time',
    log_n_entries: '{count} entries',
    log_col_to: 'To',
    log_col_subject: 'Subject',
    log_col_template: 'Template',
    log_col_by: 'By',
    log_no_entries: 'No emails logged yet.',
    log_system: 'system',
    couldnt_load_log: 'Couldn’t load log',
  },

  admin_consent: {
    title: 'Cookie consent log',
    subtitle: 'Append-only audit trail of every Accept all / Reject / re-edit decision. Use to demonstrate consent-was-given to a regulator (Thai PDPA, GDPR Art. 7).',
    filter_user_id: 'Filter by user_id',
    filter_session_id: 'Filter by session_id (anonymous UUID)',
    export_csv: 'Export page as CSV',
    n_records: '{count} records',
    col_recorded: 'Recorded',
    col_user: 'User',
    col_session: 'Session',
    col_categories: 'Categories',
    col_version: 'Version',
    col_ip: 'IP',
    no_records: 'No records.',
    guest: 'guest',
    failed_load: 'Failed to load consent log',
  },

  admin_retention: {
    title: 'Data retention',
    enabled_pre: 'Enabled — background sweep runs every',
    enabled_post: '.',
    disabled: 'Disabled — automatic sweeps are off. Edit in Config → Retention.',
    tweak_note: 'Tweak per-table windows in Config → Retention; this pane shows what\'s coming up + last run.',
    btn_run_now: 'Run sweep now',
    btn_running: 'Running…',
    confirm_run_title: 'Run a retention sweep now?',
    confirm_run_body: 'Rows older than the configured windows will be permanently deleted.',
    confirm_run_btn: 'Run sweep',
    deleted_summary: 'Deleted {count} rows in {ms}ms.',
    sweep_failed: 'Sweep failed',
    col_table: 'Table',
    col_keep_days: 'Keep (days)',
    col_total_rows: 'Total rows',
    col_would_delete: 'Would delete next sweep',
    last_sweep: 'Last sweep',
    last_sweep_summary: '{when} — finished in {ms}ms. Total deleted: {count}.',
    col_deleted: 'Deleted',
    col_note: 'Note',
  },

  admin_seed_pool: {
    chip_pool_size: 'Pool size',
    chip_per_day: 'Per day',
    chip_runway: 'Runway',
    chip_status: 'Status',
    chip_runway_sub: 'days at current rate',
    chip_status_auto: 'auto',
    chip_status_paused: 'paused',
    chip_status_sub: 'edit in Config → Seed pool',
    chip_per_day_sub: '{count} every {hours}h',
    section_title: 'Drip-feed pre-generated prompts',
    section_sub: 'Bulk-fill the pool with the Generate tool (with the queue toggle on); the background loop releases them on schedule. Manual release is a one-click nudge — useful right after filling, when the gallery is too quiet, or for testing.',
    btn_bulk_fill: 'Bulk-fill',
    btn_release_now: 'Release {count} now',
    btn_release_now_title: 'Release {count} now',
    btn_release_n: 'Release N…',
    next_up: 'Next up (FIFO)',
    next_up_sub: 'Older queued items go first. Editing? Use Prompts tab — queued rows show the same way.',
    col_title: 'Title',
    col_category: 'Category',
    col_author: 'Author',
    col_queued: 'Queued',
    pool_empty: 'Pool is empty. Click {btn} to add prompts.',
    release_prompt: 'Release how many? Pool has {count}.',
    release_summary: 'Released {released} of {requested}.',
    release_failed: 'Release failed',
    added_n: 'Added {count} to the pool',
    just_now: 'just now',
    rel_minutes: '{count}m ago',
    rel_hours: '{count}h ago',
    rel_days: '{count}d ago',
  },

  admin_online: {
    online_window: 'Online (last {min}m)',
    last_hour: 'Last hour',
    last_24h: 'Last 24 hours',
    peak_window: 'Peak (last {hours}h)',
    density_title: 'User density',
    density_sub: 'Distinct users active per {bucket} bucket. {unique} unique in this window.',
    density_sub_no_unique: 'Distinct users active per {bucket} bucket.',
    window_2h: 'Last 2 hours',
    window_6h: 'Last 6 hours',
    window_24h: 'Last 24 hours',
    window_3d: 'Last 3 days',
    window_7d: 'Last 7 days',
    active_1m: 'Active in last 1 minute',
    active_5m: 'Active in last 5 minutes',
    active_15m: 'Active in last 15 minutes',
    active_1h: 'Active in last hour',
    active_24h: 'Active in last 24 hours',
    auto_refreshes: 'Auto-refreshes every 10s',
    col_user: 'User',
    col_last_seen: 'Last seen',
    no_active: 'Nobody is active in this window.',
    no_density: 'No activity yet — bars will fill in as users browse.',
    rel_seconds: '{count}s ago',
    rel_minutes: '{count}m ago',
    rel_hours: '{count}h ago',
    rel_days: '{count}d ago',
    active_now: 'Active now',
    live: 'Live',
    auto_refresh_on: 'Auto-refresh on',
    n_users_aria: '{when} — {count} user(s)',
  },

  admin_generate_dialog: {
    eyebrow: 'Auto-generate',
    title: 'Generate prompts',
    intro: 'Mints publishable prompts from the seed library, attributed to a user you pick. Use Preview to see what will be created before committing.',
    bot_label: 'Post as @promptsave_bot (system)',
    bot_hint: 'Bot-attributed posts get a BOT badge, so readers know it\'s system content. Untick if you want to attribute to a real user instead.',
    author_label: 'Author',
    author_hint: 'this user will appear as the prompt creator',
    author_search_ph: 'Search by name, handle or email',
    author_selected: 'Selected:',
    no_users_match: 'No users match.',
    field_count: 'Count',
    field_category: 'Category',
    field_destination: 'Destination',
    queue_toggle: 'Queue for drip-feed',
    queue_note_pre: 'Queued (not live yet).',
    queue_note: 'The background releaser drains the pool on Config → Seed pool cadence (default: 1 prompt every 4h = ~6/day). See pool status under System → Seed pool.',
    publish_now: 'Publish immediately (vs save as private drafts on the author\'s profile)',
    cat_all: 'All (random mix)',
    preview_label: 'Preview ({count})',
    btn_preview: 'Preview',
    btn_loading: 'Loading…',
    btn_generate_n: 'Generate {count}',
    btn_saving: 'Saving…',
    pick_author: 'Pick an author first',
    preview_failed: 'Preview failed',
    save_failed: 'Save failed',
  },
  pricing: {
    hero_title: 'Simple pricing.',
    hero_tagline: 'Pick a plan that fits how you ship. Cancel any time.',
    cta_get_started: 'Get started',
    cta_current_plan: 'Current plan',
    cta_sign_in_first: 'Sign in to subscribe',
    disabled_message: 'Subscriptions are not available right now.',
    payment_coming_soon: 'Payment is coming soon. Contact an admin to grant access manually.',
    interval_month: '/mo',
    interval_year: '/yr',
    interval_lifetime: 'lifetime',
    feature_label_max_prompts: 'Max prompts',
    feature_label_max_collections: 'Max collections',
    feature_label_priority_support: 'Priority support',
    feature_label_api_access: 'API access',
    feature_label_no_ads: 'Ad-free experience',
    feature_label_pro_badge: 'Pro badge on profile',
    feature_label_private_prompts: 'Unlimited private prompts',
    feature_label_early_access: 'Early access to new features',
  },

  admin_subscriptions: {
    tab_plans: 'Plans',
    tab_subscribers: 'Subscribers',
    new_plan_button: 'New plan',
    no_plans: 'No plans yet — create one to get started.',
    no_subscribers: 'No subscribers match this filter.',
    n_subscribers: '{count} subscriber(s)',
    column_plan: 'Plan',
    column_price: 'Price',
    column_interval: 'Interval',
    column_subscribers: 'Subscribers',
    column_user: 'User',
    column_started: 'Started',
    column_period_end: 'Renews / ends',
    filter_active: 'Active',
    filter_cancelled: 'Cancelled',
    filter_expired: 'Expired',
    btn_cancel_sub: 'Cancel',
    confirm_delete_plan_title: 'Delete plan?',
    confirm_delete_plan: 'Permanently delete the “{name}” plan? This refuses if anyone is currently subscribed.',
    confirm_cancel_sub_title: 'Cancel subscription?',
    confirm_cancel_sub: 'Cancel the active subscription for {user}? They keep access until the period ends.',
    plan_deleted: 'Plan deleted',
    plan_saved: 'Plan saved',
    plan_created: 'Plan created',
    sub_cancelled: 'Subscription cancelled',
    sub_granted: 'Subscription granted',
    grant_title: 'Grant subscription',
    grant_user_id_ph: 'User ID (UUID)',
    grant_pick_plan: 'Pick a plan…',
    grant_period_end_hint: 'Optional: when the access ends. Leave blank for the plan default.',
    grant_btn: 'Grant',
    editor_title_new: 'New subscription plan',
    editor_title_edit: 'Edit subscription plan',
    field_slug: 'Slug',
    field_name: 'Name',
    field_description: 'Description',
    field_price: 'Price',
    field_currency: 'Currency',
    field_interval: 'Interval',
    field_features: 'Features (JSON object)',
    field_features_hint: 'Use feature_label_* i18n keys for known features (e.g. {"no_ads": true, "max_collections": 50}).',
    field_sort_order: 'Sort order',
  },

} as const;

export type TranslationKeys = typeof en;
